import { AxiosError, AxiosResponse } from 'axios';
import { useCallback, useContext, useEffect, useState } from 'react';
import LicenseContext from 'src/contexts/LicenseContext';
import PlansContext from 'src/contexts/PlansContext';
import { AllocateHistoryEntity } from 'src/entities/AllocateHistory.entity';
import { AssignTruckEntity } from 'src/entities/AssignTruck.entity';
import { PlanningEntity } from 'src/entities/planningEntity';
import { AsyncPlanningResponseEntity, PlanningResponseV5Entity } from 'src/entities/planningResponseEntity';
import { PlanningsDeliveryWithTruckAndDriverEntity } from 'src/entities/PlanningsDelivery.entity';
import { useMutationPlanningV5 } from 'src/hooks/useQueryPlanning';
import { useQueryPlanningsResultsV5 } from 'src/hooks/useQueryPlanningsResults';
import { RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

import { PlanningAction } from '../reducers';

type UsePlanningQueueParams = {
  startOn: string;
  endOn: string;
  relaxedRules: RelaxedRuleVo[];
  normalPlanning: ReturnType<typeof useMutationPlanningV5>['normalPlanning'];
  assignTruckToTermporaryDelivery: ReturnType<typeof useMutationPlanningV5>['assignTruckToTermporaryDelivery'];
  dispatchPlanning: React.Dispatch<PlanningAction>;
  handlePlanningResponse: (response: PlanningResponseV5Entity, message: string) => void;
  handleErrorResponse: (error: AxiosError<{ message: string }>) => void;
};

export const usePlanningQueue = ({
  startOn,
  endOn,
  relaxedRules,
  normalPlanning,
  assignTruckToTermporaryDelivery,
  dispatchPlanning,
  handlePlanningResponse,
  handleErrorResponse,
}: UsePlanningQueueParams) => {
  const licenseContext = useContext(LicenseContext);
  const plansContext = useContext(PlansContext);
  const { deliveryEntities, driverEntities, truckEntities, latestAlgorithmRequestVersion, generateRandomString } = plansContext;

  const [asyncPlanningResponse, setAsyncPlanningResponse] = useState<AsyncPlanningResponseEntity>(null);
  const { data: planningResultData, error: planningResultError } = useQueryPlanningsResultsV5(asyncPlanningResponse);

  const [forcePlanningQueue, setForcePlanningQueue] = useState<{ delivery: PlanningsDeliveryWithTruckAndDriverEntity, orderIds: number[], latestAlgorithmRequestVersion: number }[]>([]);
  const [currentForcePlanning, setCurrentForcePlanning] = useState<{ delivery: PlanningsDeliveryWithTruckAndDriverEntity, orderIds: number[], latestAlgorithmRequestVersion: number }>();
  const [assignTruckQueue, setAssignTruckQueue] = useState<AssignTruckEntity[]>([]);
  const [currentAssigning, setCurrentAssigning] = useState<AssignTruckEntity>();
  const [suppressLoading, setSuppressLoading] = useState(false);

  const requestForcePlanning = useCallback((delivery: PlanningsDeliveryWithTruckAndDriverEntity, requestOrderIds: number[]) => {
    if (currentForcePlanning) {
      setForcePlanningQueue((prev) => (
        [...prev, { delivery, orderIds: requestOrderIds, latestAlgorithmRequestVersion }]
          .map((it) => ({ ...it, latestAlgorithmRequestVersion }))
      ));
      return;
    }
    const { shiftId } = delivery;

    const plan: PlanningEntity = {
      startOn,
      endOn,
      lockType: 'sequence',
      isRespectRecent: false,
      excludeToll: false,
      respectType: 'day_of_week',
      respectDaysOfWeek: [],
      respectOn: '',
      isTimeTableBackward: false,
      includeFutureDeliveries: true,
      activateConstraintLoadAfterNotCleared: false,
      orderOperationIdsForSort: [],
      deleteOrderIdsFromOperations: [],
      selectedRelaxedRules: relaxedRules,
      priorityLargeTrucks: 0.5,
      expandWorkingStartTime: 0,
      expandWorkingEndTime: 0,
      expandLoadStartTime: 0,
      expandLoadEndTime: 0,
      expandUnloadStartTime: 0,
      expandUnloadEndTime: 0,
      expandMaxVolumeRate: 0.0,
      expandMaxLoadCapacityRate: 0.0,
      requestAsync: false,
      mlSourceType: -1,
      balancedLoading: false,
      orderOperationCycleIndexes: [],
      autoSplitOrders: false,
      dontExchangePreroute: true,
      concurrentAllOrNothing: false,
      loadAfterInitialStock: licenseContext.config.load_after_initial_stock,
      setCurrentTimeToAlgorithm: licenseContext.config.set_current_time_to_algorithm,
      mlPlanning: true,
      optimizeForLoad: false,
      forceNullBase: false,
      trunkTransportation: false,
      createShiftParams: shiftId === -1 ? { driverId: delivery.driverId, truckId: delivery.truckId, } : null,
      activateIncrementalMaxNumPistons: false,
      traceMode: false,
    };

    const truck = truckEntities.find(({ id }) => id === delivery.truckId);
    const driver = driverEntities.find(({ id }) => id === delivery.driverId);
    const message = `${driver?.name || ''}(${truck?.licensePlateValue || ''})に割り当てました`;

    const allocateHistoryEntity: AllocateHistoryEntity = {
      version: generateRandomString(20),
      companyId: licenseContext.config?.selected_company_id,
      actAt: new Date(),
      action: '編集',
      message,
      deliveryEntities,
    };

    setCurrentForcePlanning({ delivery, orderIds: requestOrderIds, latestAlgorithmRequestVersion });

    normalPlanning.mutate(
      {
        shiftIds: [shiftId],
        orderIds: requestOrderIds,
        planning: plan,
        latestAlgorithmRequestVersion,
        requestAsync: true,
        selectedCycleIndexes: [],
        deliveryOperations: [],
        planningSetting: null,
        forcePlanning: true,
      },
      {
        onSuccess: (data: AxiosResponse<PlanningResponseV5Entity>) => {
          dispatchPlanning({
            type: 'resetRestrictedInsertPosition',
          });
          plansContext.addAllocateHistoryEntity(allocateHistoryEntity);
          if (data.data.status === 'queued') {
            const entity: AsyncPlanningResponseEntity = data.data;
            setAsyncPlanningResponse(entity);
          } else {
            handlePlanningResponse(data.data, '修正が完了しました');
          }
        },
        onError: (e: AxiosError<{ message: string; }>) => {
          handleErrorResponse(e);
        },
        onSettled: () => {
          plansContext.setIsLoading(false);
          plansContext.setCalculating(false);
        }
      }
    );
  }, [deliveryEntities, driverEntities, endOn, handleErrorResponse, handlePlanningResponse, latestAlgorithmRequestVersion, licenseContext.config, startOn, truckEntities, currentForcePlanning, relaxedRules, dispatchPlanning, normalPlanning, plansContext, generateRandomString]);

  useEffect(() => {
    if (!currentForcePlanning) {
      const config = forcePlanningQueue[0];
      if (config && config.latestAlgorithmRequestVersion < latestAlgorithmRequestVersion) {
        requestForcePlanning(config.delivery, config.orderIds);
        setForcePlanningQueue((prev) => prev.slice(1).map((it) => ({ ...it, latestAlgorithmRequestVersion })));
      }
    }
  }, [forcePlanningQueue, requestForcePlanning, currentForcePlanning, latestAlgorithmRequestVersion]);

  useEffect(() => {
    if (!(planningResultData || planningResultError)) return;

    if (planningResultData) {
      handlePlanningResponse(planningResultData, currentForcePlanning || suppressLoading ? '' : '自動配車が完了しました');
    } else if (planningResultError) {
      handleErrorResponse(planningResultError);
    }
    setAsyncPlanningResponse(null);
    plansContext.refreshRunningEntities();
    setCurrentForcePlanning(null);
    setSuppressLoading(false);
    setCurrentAssigning(null);
  }, [handleErrorResponse, handlePlanningResponse, planningResultData, planningResultError, currentForcePlanning, suppressLoading]);

  useEffect(() => {
    const loading = asyncPlanningResponse != null && !currentForcePlanning && !suppressLoading;
    plansContext.setIsLoading(loading);
    plansContext.setCalculating(loading);
  }, [asyncPlanningResponse, currentForcePlanning, suppressLoading]);

  const assignTruckToTemporaryDeliveryRequest: (entity: AssignTruckEntity) => void = useCallback((entity) => {
    if (suppressLoading) {
      setAssignTruckQueue((prev) => ([...prev, entity].map((it) => ({ ...it, latestAlgorithmRequestVersion }))));
      return;
    }
    setCurrentAssigning({ ...entity, latestAlgorithmRequestVersion });
    setSuppressLoading(true);
    assignTruckToTermporaryDelivery.mutate({
      startOn, endOn, deliveryIds: entity.deliveryIds, truckId: entity.truckId, driverId: entity.driverId, latestAlgorithmRequestVersion,
    }, {
      onSuccess: (data: AxiosResponse<PlanningResponseV5Entity>) => {
        if (data.data.status === 'queued') {
          setAsyncPlanningResponse(data.data);
        } else {
          handlePlanningResponse(data.data, '');
        }
      },
      onError: (e: AxiosError<{ message: string; }>) => {
        setCurrentAssigning(null);
        setSuppressLoading(false);
        handleErrorResponse(e);
      },
    });
  }, [endOn, handleErrorResponse, handlePlanningResponse, latestAlgorithmRequestVersion, startOn, suppressLoading, assignTruckToTermporaryDelivery]);

  useEffect(() => {
    if (!suppressLoading && assignTruckQueue.length > 0) {
      const data = assignTruckQueue[0];
      if ((data && data.latestAlgorithmRequestVersion < latestAlgorithmRequestVersion) || currentAssigning === null) {
        assignTruckToTemporaryDeliveryRequest(data);
        setAssignTruckQueue((prev) => prev.slice(1).map((it) => ({ ...it, latestAlgorithmRequestVersion })));
      }
    }
  }, [assignTruckToTemporaryDeliveryRequest, assignTruckQueue, latestAlgorithmRequestVersion, suppressLoading, currentAssigning]);

  const resetQueue = useCallback(() => {
    setCurrentForcePlanning(null);
    setSuppressLoading(false);
    setCurrentAssigning(null);
  }, []);

  return {
    asyncPlanningResponse,
    setAsyncPlanningResponse,
    forcePlanningQueue,
    currentForcePlanning,
    assignTruckQueue,
    currentAssigning,
    suppressLoading,
    requestForcePlanning,
    assignTruckToTemporaryDeliveryRequest,
    resetQueue,
  };
};
